// REACT
import { JSX, JSXElementConstructor, ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// YARN
import {
  GridActionsCellItemProps,
  GridCellParams,
  GridColDef,
  GridColumnVisibilityModel,
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridRowId,
  GridRowModel,
  GridRowParams,
  GridRowsProp,
  GridSortModel,
  GridToolbarProps,
  GridValueOptionsParams,
  ToolbarPropsOverrides,
} from '@mui/x-data-grid-pro';
import { Dayjs } from 'dayjs';
import { toast } from 'react-toastify';
// SERVICES
import CustomDataGrid from 'utils/Datagrid/CustomCultureDataGrid';
import {
  inputAbsolutePositiveFloatValidator,
  inputNumberRequired,
  inputPositiveFloatValidator,
  inputRequired,
  parcelConversionDateChecker,
} from 'components/generics/Validators/validator';
import { CustomHeaderWithoutSort, EditInputCell } from 'utils/Datagrid/CustomCultureDataGridUtils';
import {
  CustomSortableHeader,
  childrenTotalSurface,
  getFamily,
  processColumns,
  totalSurfaceComparator,
  resetErrorsForm,
} from 'utils/Datagrid/CustomGenericDataGridUtils';
import useDataGridParcelsActions from 'utils/Datagrid/useDataGridParcelsActions';
import {
  checkDecimalRegexTwoDigitsDatagridValue,
  checkTypeStringRegexDatagrid,
  getOnlyAttributeUpdated,
  sortByLocalName,
} from 'utils/tools_functions';
import { validateDatagridData } from '../../generics/Validators/datagrid.validate';
import { isSameAsOriginal } from 'utils/Datagrid/CustomForecastDataGridUtils';
import {
  CustomConversionDateParcelCell,
  CustomEditConversionDateCell,
  CustomEditOrganicStateCell,
  CustomParcelCell,
  CustomParcelSurfaceCell,
  customIsIrrigableCell,
  customIsOrganicCell,
  getCultureCampaignKey,
} from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { ORGANIC_STATES } from 'utils/GlobalVariables';
// INTERFACES
import { IParcelsList } from 'components/generics/Interface/Api/Response/Exploitation/Parcels/IParcelsList';
import { IGroundType } from 'components/generics/Interface/Api/Response/GroundType/IGroundType';
import { ICulture } from 'components/generics/Interface/ICulture';
import { IMetadata } from 'components/generics/Interface/Api/Response/Metadata/IMetadata';
import { IFormErrors } from 'components/generics/Interface/Commons/IErrorForm';
import { IStackedParcelsRequestsRef } from 'components/generics/Interface/Exploitation/Parcels/IStackedParcelsRequestsRef';
// THEMES
import './dataGridParcels.style.scss';
import { ICampaign } from 'components/generics/Interface/Exploitation/Parcels/ICampaign';

type DataGridParcelsParams = {
  cultures: ICulture[];
  groundTypes: IGroundType[];
  id: number;
  forecastCampaign: ICampaign;
  parcels?: IParcelsList[];
  refreshData: Function;
  dataGridLoading: boolean;
  setEditData: Function;
  setModalGroup?: Function;
  columnsMetadata: IMetadata[];
  stackedParcelsRequestsRef: React.RefObject<IStackedParcelsRequestsRef[]>;
  setIsAllowedSubmit?: Function;
  maxDate: Dayjs;
  getActions?: (
    id: GridRowId,
  ) => React.ReactElement<GridActionsCellItemProps, string | React.JSXElementConstructor<any>>[];
  customToolbar?: JSX.Element;
};
const DataGridParcels = ({
  id,
  groundTypes,
  cultures,
  forecastCampaign,
  parcels,
  refreshData,
  dataGridLoading,
  setEditData,
  setModalGroup,
  columnsMetadata,
  stackedParcelsRequestsRef,
  setIsAllowedSubmit,
  maxDate,
  getActions,
  customToolbar,
}: DataGridParcelsParams) => {
  const { t } = useTranslation();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'name',
      sort: 'asc',
    },
  ]);
  const [errorsFormDefault, setErrorsFormDefault] = useState<IFormErrors>({
    formError: false,
    name: {
      message: '',
      validator: [inputRequired],
    },
    surface: {
      message: '',
      validator: [inputNumberRequired, inputAbsolutePositiveFloatValidator],
    },
    conversionDate: {
      message: '',
      validator: [parcelConversionDateChecker(forecastCampaign?.startYear)],
    },
  });
  const [columnsVisible, setColumnsVisible] = useState<GridColumnVisibilityModel>({
    activityStart: false,
    activityEnd: false,
  });
  const [rows, setRows] = useState<GridRowsProp | []>([]);
  const [openOrganicStateDialog, setOpenOrganicStateDialog] = useState(false);

  const isOpen = errorsFormDefault.formError;

  type ProcessedRowType = {
    id: number;
    name: string;
    surface: number;
    totalSurface: number | undefined;
    exploitationId: number;
    conversionDate: string | undefined;
    organicState: string | undefined;
    isIrrigable: boolean;
    groundType: string;
    previousCulture2: string;
    previousCulture1: string;
    parentId: undefined | number[];
    isParent: boolean;
    activityStart: number | undefined;
    activityEnd: number | undefined;
  };

  function isIGroundType(object: any): object is IGroundType {
    return 'name' in object;
  }

  // parcel?.parcelCultures[campaignKey]?.name ?? 'Pas de culture associée',

  const processedRows: (ProcessedRowType | undefined)[] | undefined = parcels?.map(
    (parcel: IParcelsList) => {
      if (isIGroundType(parcel.groundType)) {
        return {
          id: parcel?.id,
          name: parcel.name,
          surface: parcel?.surface,
          totalSurface: parcel?.isParent
            ? childrenTotalSurface(parcel?.id, parcels) + parcel?.surface
            : parcel?.surface,
          exploitationId: id,
          conversionDate: parcel?.conversionDate,
          organicState: parcel?.organicState,
          isIrrigable: parcel?.isIrrigable,
          groundType: parcel?.groundType?.name,
          previousCulture2:
            parcel?.parcelCultures?.[getCultureCampaignKey(2, forecastCampaign)]?.name ??
            t('message.noCultureAssociated'),
          previousCulture1:
            parcel?.parcelCultures?.[getCultureCampaignKey(1, forecastCampaign)]?.name ??
            t('message.noCultureAssociated'),
          parentId: parcel?.parent ? getFamily(parcel, parcels) : [parcel.id],
          isParent: parcel?.isParent ?? false,
          activityStart: parcel?.activityStart,
          activityEnd: parcel?.activityEnd,
        };
      }
    },
  );

  useEffect(() => {
    if (processedRows) {
      const checkedProcessedRows = processedRows.filter(
        (row) => row !== undefined,
      ) as ProcessedRowType[];
      setRows(checkedProcessedRows);
    }
  }, [parcels]);

  const {
    rowModesModel,
    setRowModesModel,
    renderDeleteDialog,
    getActionsParcels,
    renderDeletegroupingDialog,
    renderOrganicStateModal,
    setModalProps,
  } = useDataGridParcelsActions(
    rows,
    refreshData,
    errorsFormDefault,
    setEditData,
    setModalGroup,
    openOrganicStateDialog,
  );

  const renderEditConversionDateCell = (params: GridRenderEditCellParams, format: string) => {
    return (
      <CustomEditConversionDateCell
        params={params}
        maxDate={maxDate}
        errorsFormDefault={errorsFormDefault}
        isOpen={isOpen}
        format={format}
      />
    );
  };

  // const renderEditActivityDateCell = (params: GridRenderEditCellParams) => {
  //   return (
  //     <CustomEditConversionDateCell
  //       params={params}
  //       errorsFormDefault={errorsFormDefault}
  //       isOpen={isOpen}
  //     />
  //   );
  // };

  const renderEditOrganicStateCell = (params: GridRenderEditCellParams) => {
    return (
      <CustomEditOrganicStateCell
        params={params}
        setOpenOrganicStateDialog={setOpenOrganicStateDialog}
        setModalProps={setModalProps}
      />
    );
  };
  const findRightActions: (
    params: GridRowParams,
  ) => React.ReactElement<GridActionsCellItemProps, string | React.JSXElementConstructor<any>>[] = (
    params,
  ) => {
    if (!getActions) {
      return getActionsParcels(params.id, params.row);
    } else {
      return getActions(params.id);
    }
  };

  const columnsDefinition: GridColDef[] = [
    {
      field: 'name',
      headerName: t('common.parcel'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      sortable: false,
      editable: true,
      valueParser: (value: string) => {
        return checkTypeStringRegexDatagrid(value);
      },

      cellClassName: (params: GridCellParams<any, number>) =>
        params.cellMode === 'view' && params.row.parentId.length > 1 ? 'first-children' : '',
      renderCell: (params: GridRenderCellParams) =>
        CustomParcelCell(params, stackedParcelsRequestsRef),
      renderEditCell: (props: GridRenderCellParams) =>
        EditInputCell(props, errorsFormDefault, isOpen),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'name'}
          name={t('common.parcel')}
        />
      ),
    },
    {
      field: 'surface',
      headerName: t('common.surfaceHa'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 0.8,
      sortable: false,
      editable: true,
      valueParser: (value: string) => {
        return checkDecimalRegexTwoDigitsDatagridValue(value);
      },
      valueGetter: (value, row) => ({
        totalSurface: row?.totalSurface,
        surface: row?.surface,
      }),
      sortComparator: totalSurfaceComparator,
      renderCell: (params: GridRenderCellParams) =>
        CustomParcelSurfaceCell(params, stackedParcelsRequestsRef),
      cellClassName: (params: GridCellParams<any, number>) =>
        params.row.parentId.length > 1 ? 'parcel_children' : '',
      renderEditCell: (props: GridRenderCellParams) =>
        EditInputCell(props, errorsFormDefault, isOpen),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'surface'}
          name={t('common.surfaceHa')}
        />
      ),
    },
    {
      field: 'conversionDate',
      headerName: t('column.transitionDate'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      editable: true,
      flex: 1.1,
      sortable: false,
      renderEditCell: (params: GridRenderEditCellParams) =>
        renderEditConversionDateCell(params, 'YYYY-MM-DD'),
      renderCell: (params: GridRenderCellParams) =>
        CustomConversionDateParcelCell(params, stackedParcelsRequestsRef, t),
      cellClassName: (params: GridCellParams<any, number>) =>
        params.row.parentId.length > 1 ? 'parcel_children' : '',
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'conversionDate'}
          name={t('column.transitionDate')}
        />
      ),
    },
    {
      field: 'organicState',
      headerName: t('organicState.organic'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      type: 'boolean',
      editable: true,
      flex: 0.6,
      sortable: false,
      renderEditCell: renderEditOrganicStateCell,
      renderCell: (params: GridRenderCellParams) =>
        customIsOrganicCell(params, stackedParcelsRequestsRef),
      cellClassName: (params: GridCellParams<any, number>) =>
        params.row.parentId.length > 1 ? 'parcel_children' : '',
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'organicState'}
          name={t('organicState.organic')}
        />
      ),
      valueGetter: (value) => value === ORGANIC_STATES.organic,
      valueSetter: (value, row) => {
        return {
          ...row,
          organicState: value
            ? ORGANIC_STATES.organic
            : row?.organicState === ORGANIC_STATES.conversion
              ? ORGANIC_STATES.conversion
              : ORGANIC_STATES.conventional,
        };
      },
    },
    {
      field: 'isIrrigable',
      headerName: t('common.irrigability'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      type: 'boolean',
      editable: true,
      flex: 0.6,
      sortable: false,
      renderCell: (params: GridRenderCellParams) =>
        customIsIrrigableCell(params, stackedParcelsRequestsRef),
      cellClassName: (params: GridCellParams<any, number>) =>
        params.row.parentId.length > 1 ? 'parcel_children' : '',
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'isIrrigable'}
          name={t('common.irrigability')}
        />
      ),
    },
    {
      field: 'groundType',
      headerName: t('column.soilTypes'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      sortable: false,
      editable: true,
      type: 'singleSelect',
      cellClassName: (params: GridCellParams<any, number>) =>
        params.row.parentId.length > 1 ? 'parcel_children' : '',

      valueOptions: () => {
        if (groundTypes) {
          return [
            ...sortByLocalName(groundTypes)?.map((groundType: IGroundType) => groundType.name),
          ];
        }
        return [];
      },
      renderCell: (params: GridRenderCellParams) =>
        CustomParcelCell(params, stackedParcelsRequestsRef),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'groundType'}
          name={t('column.soilTypes')}
        />
      ),
    },
    {
      field: 'previousCulture2',
      headerName: forecastCampaign
        ? `${forecastCampaign.startYear - 2}-${forecastCampaign.endYear - 2}`
        : undefined,
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      sortable: false,
      editable: true,
      type: 'singleSelect',
      cellClassName: (params: GridCellParams<any, number>) =>
        params.row.parentId.length > 1 ? 'parcel_children' : '',
      valueOptions: (params: GridValueOptionsParams<any>) => {
        if (cultures) {
          if (params.row.previousCulture2 === t('message.noCultureAssociated')) {
            return [...cultures?.map((culture) => culture.name), t('message.noCultureAssociated')];
          }
          return [...cultures?.map((culture) => culture.name)];
        }
        return [];
      },
      renderCell: (params: GridRenderCellParams) =>
        CustomParcelCell(params, stackedParcelsRequestsRef),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'previousCulture2'}
          name={
            forecastCampaign
              ? `${forecastCampaign.startYear - 2}-${forecastCampaign.endYear - 2}`
              : t('column.cultureN1')
          }
        />
      ),
    },
    {
      field: 'previousCulture1',
      headerName: forecastCampaign
        ? `${forecastCampaign.startYear - 1}-${forecastCampaign.endYear - 1}`
        : undefined,
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      flex: 1,
      sortable: false,
      editable: true,
      type: 'singleSelect',
      cellClassName: (params: GridCellParams<any, number>) =>
        params.row.parentId.length > 1 ? 'parcel_children' : '',
      valueOptions: (params: GridValueOptionsParams<any>) => {
        if (cultures) {
          if (params.row.previousCulture1 === t('message.noCultureAssociated')) {
            return [...cultures?.map((culture) => culture.name), t('message.noCultureAssociated')];
          }
          return [...cultures?.map((culture) => culture.name)];
        }
        return [];
      },
      renderCell: (params: GridRenderCellParams) =>
        CustomParcelCell(params, stackedParcelsRequestsRef, forecastCampaign),
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'previousCulture1'}
          name={
            forecastCampaign
              ? `${forecastCampaign.startYear - 1}-${forecastCampaign.endYear - 1}`
              : t('column.cultureN')
          }
        />
      ),
    },
    {
      field: 'activityStart',
      headerName: t('column.activityStartDate'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      editable: true,
      flex: 1.1,
      sortable: false,
      renderEditCell: (params: GridRenderEditCellParams) =>
        renderEditConversionDateCell(params, 'YYYY'),
      renderCell: (params: GridRenderCellParams) =>
        CustomConversionDateParcelCell(params, stackedParcelsRequestsRef, t),
      cellClassName: (params: GridCellParams<any, number>) =>
        params.row.parentId.length > 1 ? 'parcel_children' : '',
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'activityStart'}
          name={t('column.activityStartDate')}
        />
      ),
    },
    {
      field: 'activityEnd',
      headerName: t('column.activityEndDate'),
      headerClassName: 'forecast-header',
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      editable: true,
      flex: 1.1,
      sortable: false,
      renderEditCell: (params: GridRenderEditCellParams) =>
        renderEditConversionDateCell(params, 'YYYY'),
      renderCell: (params: GridRenderCellParams) =>
        CustomConversionDateParcelCell(params, stackedParcelsRequestsRef, t),
      cellClassName: (params: GridCellParams<any, number>) =>
        params.row.parentId.length > 1 ? 'parcel_children' : '',
      renderHeader: () => (
        <CustomSortableHeader
          setSortModel={setSortModel}
          fieldName={'activityEnd'}
          name={t('column.activityEndDate')}
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('common.actions'),
      flex: 1,
      minWidth: 160,
      headerAlign: 'center',
      align: 'center',
      cellClassName: 'actions',
      getActions: (params: GridRowParams) => findRightActions(params),
      renderHeader: () => <CustomHeaderWithoutSort name={t('common.actions')} />,
    },
  ];

  const processedColumns = processColumns(columnsDefinition, columnsMetadata);

  const handleRowUpdateValidation = (row: GridRowModel, errorsFormDefault: IFormErrors) => {
    return new Promise((resolve, reject) => {
      if (errorsFormDefault.formError === false) {
        resolve(row);
      } else {
        reject();
      }
    });
  };

  const processRowUpdate = useCallback(
    async (updatedRow: GridRowModel, previousRow: GridRowModel) => {
      errorsFormDefault.formError = false;
      if (typeof updatedRow.surface === 'object') {
        updatedRow.surface = updatedRow.surface.surface;
      }
      const toUpdate: IStackedParcelsRequestsRef = getOnlyAttributeUpdated(previousRow, updatedRow);

      // Logic to check if we can update the organicState
      // If the previous organicState is not the same than the updated one
      if (previousRow.organicState !== updatedRow.organicState) {
        // If the updated organicState is not organic
        // And if the previous organicState is not conventional or conversion
        if (
          updatedRow.organicState !== ORGANIC_STATES.organic &&
          (previousRow.organicState === ORGANIC_STATES.conventional ||
            previousRow.organicState === ORGANIC_STATES.conversion)
        ) {
          delete toUpdate.organicState;
        }
      }
      setErrorsFormDefault({
        ...validateDatagridData(toUpdate, errorsFormDefault, processedColumns, updatedRow.id),
      });

      if (errorsFormDefault.formError && Object.keys(rowModesModel).length > 1) {
        resetErrorsForm(errorsFormDefault);
        return previousRow;
      }

      if (Object.entries(toUpdate).length > 0) {
        await handleRowUpdateValidation(updatedRow, errorsFormDefault);

        let existInStackedRequest = false;
        stackedParcelsRequestsRef.current.forEach((request) => {
          if (request?.id === updatedRow.id) {
            existInStackedRequest = true;
          }
        });

        if (!existInStackedRequest) {
          stackedParcelsRequestsRef.current.push({ ...toUpdate, id: updatedRow.id });
        }

        stackedParcelsRequestsRef.current = stackedParcelsRequestsRef.current.map((request) => {
          if (request?.id === updatedRow.id) {
            return {
              ...request,
              ...toUpdate,
            };
          } else {
            return request;
          }
        });

        Object.keys(toUpdate).forEach((field) => {
          if (isSameAsOriginal(field, parcels, updatedRow)) {
            stackedParcelsRequestsRef.current = stackedParcelsRequestsRef.current.map((request) => {
              if (request?.id === updatedRow.id) {
                delete request[field as keyof IStackedParcelsRequestsRef];
                delete toUpdate[field as keyof IStackedParcelsRequestsRef];
                return {
                  ...request,
                  ...toUpdate,
                };
              } else {
                return request;
              }
            });
          }
        });

        stackedParcelsRequestsRef.current = stackedParcelsRequestsRef.current.filter(
          (request) => Object.keys(request).length > 1,
        );

        // trigger warning if no conversionDate is defined and parcel is going to be organic after update
        const { conversionDate, organicState } = toUpdate || {};
        const { conversionDate: stackedConversionDate, organicState: stackedOrganicState } =
          stackedParcelsRequestsRef?.current?.find((request) => request?.id === updatedRow?.id) ||
          {};

        const previousConversionDate = parcels?.find(
          (parcel) => parcel.id === updatedRow.id,
        )?.conversionDate;

        const isConversionDateMissing =
          !conversionDate && !stackedConversionDate && !previousConversionDate;

        const isBecomingOrganic =
          organicState === ORGANIC_STATES.organic || stackedOrganicState === ORGANIC_STATES.organic;
        const isConversionOrOrganicInUpdate =
          'conversionDate' in toUpdate || 'organicState' in toUpdate;

        if (isConversionDateMissing && isBecomingOrganic && isConversionOrOrganicInUpdate) {
          toast.warning(t('message.autoTransitionDate'));
        }

        setIsAllowedSubmit?.(stackedParcelsRequestsRef.current.length ? true : false);

        const updatedSurface = Number(updatedRow.surface);
        const totalSurface = updatedRow?.isParent
          ? childrenTotalSurface(updatedRow?.id, parcels) + updatedSurface
          : updatedSurface;

        updatedRow.surface = updatedSurface;
        updatedRow.totalSurface = totalSurface;

        setRows(rows.map((row) => (row.id === updatedRow.id ? updatedRow : row)));

        return updatedRow;
      }

      return previousRow;
    },
    [parcels, rowModesModel],
  );

  return (
    rows && (
      <div className="parcel_datagrid_container">
        <CustomDataGrid
          rows={rows}
          columns={processedColumns}
          processRowUpdate={processRowUpdate}
          loading={dataGridLoading}
          sortModel={sortModel}
          rowModesModel={rowModesModel}
          setRowModesModel={setRowModesModel}
          fromParcel={true}
          columnVisibilityModel={columnsVisible}
          setColumnVisibilityModel={setColumnsVisible}
          openOrganicStateDialog={openOrganicStateDialog}
          customToolbar={customToolbar}
        />
        {renderDeleteDialog()}
        {renderDeletegroupingDialog()}
        {renderOrganicStateModal()}
      </div>
    )
  );
};

export default DataGridParcels;
